import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { FieldInspectionStatus } from "../../domain/field-control/field-inspection-progress.js";
import type { FieldControlLineRow, FieldControlScope } from "../../application/services/field-control-service.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";

/**
 * CONTROLE DE CAMPO — acompanhamento do avanço das inspeções por Linha.
 *
 *   MINHAS LINHAS   o Inspetor aponta o avanço das Linhas dos seus Sistemas;
 *   ACOMPANHAMENTO  Técnico/Engenheiro acompanham qualquer Inspetor;
 *   GERENCIAMENTO   vínculo Inspetor → Sistema, em cascata por IDs reais.
 *
 * Nenhuma regra de negócio vive aqui: status, datas, motivo obrigatório,
 * concorrência e escopo do Inspetor são decididos no serviço. A tela só
 * apresenta e envia a intenção do usuário.
 *
 * A aba visível depende da permissão, nunca do nome do papel.
 */

type Tab = "minhas" | "acompanhamento" | "gerenciamento";

const TABS: ReadonlyArray<[Tab, string, string]> = [
  ["minhas", "MINHAS LINHAS", PermissionCode.FieldControlViewOwn],
  ["acompanhamento", "ACOMPANHAMENTO", PermissionCode.FieldControlViewAll],
  ["gerenciamento", "GERENCIAMENTO", PermissionCode.FieldControlManageAssignments]
];

interface Option { id: string; label: string }

class AppFieldControlPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private tab: Tab = "minhas";
  private scope: FieldControlScope | null = null;
  private loading = false;
  private message: { kind: "ok" | "error" | "info"; text: string } | null = null;
  private selected = new Set<string>();

  // Filtros do acompanhamento (item 27).
  private filterInspector = "";
  private filterSystem = "";
  private filterStatus = "";
  private search = "";

  // Cascata do gerenciamento (item 5).
  private clients: Option[] = [];
  private workOrders: Option[] = [];
  private projects: Option[] = [];
  private unitAreas: Option[] = [];
  private systems: Option[] = [];
  private lines: Option[] = [];
  private inspectors: Option[] = [];
  private assignments: Array<{ id: string; inspectorProfessionalId: string; targetType: "SYSTEM" | "LINE"; targetId: string; systemId: string | null; active: boolean }> = [];
  private form = { clientId: "", serviceOrderId: "", projectId: "", unitAreaId: "", targetType: "SYSTEM" as "SYSTEM" | "LINE", targetId: "", systemId: "", inspectorProfessionalId: "" };

  public async connectedCallback(): Promise<void> {
    const requested = (new URLSearchParams(location.search).get("tab") || "").toLowerCase() as Tab;
    if (TABS.some(([id]) => id === requested) && this.can(this.permissionOfTab(requested))) this.tab = requested;
    else this.tab = this.firstVisibleTab();
    this.render();
    await this.reload();
  }

  private get platform(): any {
    return (window as any).app?.platform;
  }

  /**
   * [COMANDO ÚNICO] Contexto de requisição. Aceita clientId opcional para
   * telas com filtro local próprio, eliminando dependência do seletor global.
   */
  private ctx(clientId?: string): any {
    const current = contextStore.get();
    return {
      activeClientId: clientId || current.activeClientId || null,
      correlationId: "",
      userId: current.userId,
      professionalId: current.professionalId || null,
      permissions: current.permissions
    };
  }

  private can(permission: string): boolean {
    return contextStore.hasPermission(permission as never);
  }

  private permissionOfTab(tab: Tab): string {
    return TABS.find(([id]) => id === tab)?.[2] ?? PermissionCode.FieldControlViewOwn;
  }

  private visibleTabs(): ReadonlyArray<[Tab, string, string]> {
    return TABS.filter(([, , permission]) => this.can(permission));
  }

  private firstVisibleTab(): Tab {
    return this.visibleTabs()[0]?.[0] ?? "minhas";
  }

  private setTab(tab: Tab): void {
    this.tab = tab;
    this.selected.clear();
    this.message = null;
    const url = new URL(location.href);
    url.searchParams.set("tab", tab);
    history.replaceState(null, "", url.toString());
    this.render();
    void this.reload();
  }

  // ------------------------------------------------------------- dados

  private async reload(): Promise<void> {
    this.loading = true;
    this.render();
    try {
      if (this.tab === "gerenciamento") await this.loadManagement();
      else await this.loadScope();
    } catch (error) {
      this.message = { kind: "error", text: this.errorText(error) };
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private async loadScope(): Promise<void> {
    const service = this.platform?.fieldControlService;
    if (!service) return;
    this.scope = this.tab === "minhas"
      ? await service.listMyLines(this.ctx())
      : await service.listScope(this.ctx(), {
          ...(this.filterInspector ? { inspectorProfessionalId: this.filterInspector } : {}),
          ...(this.filterSystem ? { systemId: this.filterSystem } : {})
        });
    if (this.tab === "acompanhamento" && this.inspectors.length === 0) await this.loadInspectors();
  }

  private async loadInspectors(): Promise<void> {
    try {
      // [FONTE ÚNICA] Consulta oficial do domínio de Controle de Campo.
      // Reutiliza ELIGIBLE_INSPECTOR_TYPES (mesma regra de createAssignment).
      // Inspetores são PROFESSIONALS GLOBAIS: o serviço NÃO filtra por clientId.
      const professionals = await this.platform?.fieldControlService?.listEligibleInspectors(this.ctx());
      this.inspectors = (professionals || []).map((p: any) => ({ id: String(p.id), label: p.name || String(p.id) }));
    } catch { /* lista de apoio: a ausência não bloqueia a tela */ }
  }

  private async loadManagement(): Promise<void> {
    // [COMANDO ÚNICO] Carrega clientes primeiro sem filtro para popular o select local
    const ctxGlobal = this.ctx();
    const clients = await this.ctxLoad.load("Clientes", () => this.platform?.adminService?.listClients(ctxGlobal));
    this.clients = (clients || []).map((c: any) => ({ id: String(c.id), label: c.name || c.legalName || String(c.id) }));
    // Define clientId local a partir do form ou do primeiro cliente disponível (sem depender do seletor global)
    if (!this.form.clientId) this.form.clientId = this.clients[0]?.id || "";
    // Agora usa o clientId local para carregar assignments e inspetores
    const ctxLocal = this.ctx(this.form.clientId);
    const assignments = await this.platform?.fieldControlService?.listAssignments(ctxLocal).catch(() => []);
    this.assignments = assignments || [];
    await Promise.all([this.loadInspectors(), this.loadCascade()]);
  }

  /** Cascata CLIENTE → OS → PROJETO → UNIDADE/ÁREA → Sistema ou Linha. */
  private async loadCascade(): Promise<void> {
    const clientId = this.form.clientId;
    const ctx = this.ctx(clientId);
    if (!clientId) { this.workOrders = []; this.projects = []; this.unitAreas = []; this.systems = []; this.lines = []; return; }
    try {
      const [workOrders, projects] = await Promise.all([
        await this.ctxLoad.load("Ordens de Serviço", () => this.platform?.adminService?.listWorkOrders(ctx, clientId)),
        await this.ctxLoad.load("Projetos", () => this.platform?.projectService?.listProjects(clientId))
      ]);
      this.workOrders = (workOrders || []).map((w: any) => ({ id: String(w.id), label: w.number || w.code || String(w.id) }));
      this.projects = (projects || []).map((p: any) => ({ id: String(p.id), label: `${p.code || ""} ${p.name || ""}`.trim() || String(p.id) }));

      const unitAreas = this.form.projectId
        ? await this.ctxLoad.load("Unidades / Áreas", () => this.platform?.projectService?.listUnitAreas({ projectId: this.form.projectId, clientId }))
        : [];
      this.unitAreas = (unitAreas || []).map((u: any) => ({ id: String(u.id), label: `${u.code || ""} ${u.name || ""}`.trim() || String(u.id) }));

      const equipments = await this.ctxLoad.load("Equipamentos", () => this.platform?.equipmentService?.listEquipments(ctx, { status: "ACTIVE", clientId }));
      this.systems = (equipments || [])
        .filter((e: any) => e.equipmentType === "PIPING_SYSTEM"
          && (!this.form.projectId || String(e.projectId || "") === this.form.projectId)
          && (!this.form.unitAreaId || String(e.unitAreaId || e.areaId || "") === this.form.unitAreaId))
        .map((e: any) => ({ id: String(e.id), label: e.tag || String(e.id) }));
      this.lines = (equipments || [])
        .filter((e: any) => e.equipmentType === "PIPING_LINE"
          && e.pipeSystemApplicability === "NOT_APPLICABLE"
          && (!this.form.projectId || String(e.projectId || "") === this.form.projectId)
          && (!this.form.unitAreaId || String(e.unitAreaId || e.areaId || "") === this.form.unitAreaId))
        .map((e: any) => ({ id: String(e.id), label: e.tag || String(e.id) }));
    } catch (error) {
      this.message = { kind: "error", text: this.errorText(error) };
    }
  }

  // ------------------------------------------------------------- ações

  private rowOf(lineEquipmentId: string): FieldControlLineRow | undefined {
    return this.scope?.rows.find(row => row.lineEquipmentId === lineEquipmentId);
  }

  private async applyChange(lineEquipmentId: string, change: Record<string, unknown>): Promise<void> {
    const row = this.rowOf(lineEquipmentId);
    if (!row) return;
    try {
      await this.platform.fieldControlService.updateProgress(this.ctx(), {
        lineEquipmentId,
        expectedVersion: row.progress.updatedBy === null && row.progress.version === 1 ? 0 : row.progress.version,
        change
      });
      this.message = { kind: "ok", text: `Linha ${row.lineTag} atualizada.` };
    } catch (error) {
      this.message = { kind: "error", text: this.errorText(error) };
    }
    await this.reload();
  }

  private async bulk(change: Record<string, unknown>, confirmText: string): Promise<void> {
    if (this.selected.size === 0) {
      this.message = { kind: "info", text: "Selecione ao menos uma Linha." };
      this.render();
      return;
    }
    if (!confirm(`${confirmText} (${this.selected.size} Linha(s))`)) return;
    try {
      const result = await this.platform.fieldControlService.updateProgressBulk(this.ctx(), [...this.selected], change);
      this.message = result.failures.length > 0
        ? { kind: "error", text: `${result.updated.length} atualizada(s); ${result.failures.length} falharam: ${result.failures.map((f: any) => f.message).join(" | ")}` }
        : { kind: "ok", text: `${result.updated.length} Linha(s) atualizada(s).` };
    } catch (error) {
      this.message = { kind: "error", text: this.errorText(error) };
    }
    this.selected.clear();
    await this.reload();
  }

  private errorText(error: unknown): string {
    return (error as { safeMessage?: string })?.safeMessage || (error as Error)?.message || "Falha na operação.";
  }

  // ------------------------------------------------------------- render

  private render(): void {
    if (this.visibleTabs().length === 0) {
      this.innerHTML = `
      ${this.ctxLoad.banner()}<div class="fc-empty">Você não tem permissão para acessar o Controle de Campo.</div><style>${this.styles()}</style>
      <style>${CONTEXT_LOAD_STYLES}</style>`;
      return;
    }
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="fc">
        <header class="fc-head">
          <div>
            <h1 class="fc-title">CONTROLE DE CAMPO</h1>
            <p class="fc-sub">Avanço das inspeções por Linha de Tubulação. 100% = Linha concluída.</p>
          </div>
        </header>
        <nav class="fc-tabs">
          ${this.visibleTabs().map(([id, label]) => `<button class="fc-tab ${this.tab === id ? "active" : ""}" data-tab="${id}" type="button">${label}</button>`).join("")}
        </nav>
        ${this.message ? `<div class="fc-notice ${this.message.kind}">${this.esc(this.message.text)}</div>` : ""}
        ${this.loading ? `<div class="fc-empty">Carregando…</div>` : this.tabHtml()}
      </div>`;
    this.bind();
  }

  private tabHtml(): string {
    if (this.tab === "gerenciamento") return this.managementHtml();
    return `${this.summaryHtml()}${this.filtersHtml()}${this.bulkBarHtml()}${this.tableHtml()}`;
  }

  /** Cards do topo (item 28). */
  private summaryHtml(): string {
    const s = this.scope?.summary;
    if (!s) return "";
    const card = (label: string, value: string, tone = "") => `<div class="fc-card ${tone}"><span class="fc-card-label">${label}</span><strong class="fc-card-value">${value}</strong></div>`;
    return `
      <div class="fc-cards">
        ${card("LINHAS NO ESCOPO", String(s.total))}
        ${card("NÃO INICIADAS", String(s.notStarted))}
        ${card("EM ANDAMENTO", String(s.inProgress), "warn")}
        ${card("CONCLUÍDAS", String(s.completed), "ok")}
        ${card("FORA DE OPERAÇÃO", String(s.outOfOperation), "muted")}
        ${card("AVANÇO REAL", `${this.formatPercent(s.realProgressPercent)}%`, "accent")}
      </div>`;
  }

  private filtersHtml(): string {
    const statusOptions = [FieldInspectionStatus.NotStarted, FieldInspectionStatus.InProgress, FieldInspectionStatus.Completed, FieldInspectionStatus.OutOfOperation];
    return `
      <div class="fc-filters">
        ${this.tab === "acompanhamento" ? `
          <label class="fc-field"><span>INSPETOR</span>
            <select id="f-inspector">
              <option value="">Todos</option>
              ${this.inspectors.map(i => `<option value="${this.esc(i.id)}" ${this.filterInspector === i.id ? "selected" : ""}>${this.esc(i.label)}</option>`).join("")}
            </select>
          </label>` : ""}
        <label class="fc-field"><span>SISTEMA</span>
          <select id="f-system">
            <option value="">Todos</option>
            ${(this.scope?.systems || []).map(s => `<option value="${this.esc(s.id)}" ${this.filterSystem === s.id ? "selected" : ""}>${this.esc(s.tag)}</option>`).join("")}
          </select>
        </label>
        <label class="fc-field"><span>STATUS</span>
          <select id="f-status">
            <option value="">Todos</option>
            ${statusOptions.map(s => `<option value="${this.esc(s)}" ${this.filterStatus === s ? "selected" : ""}>${this.esc(s)}</option>`).join("")}
          </select>
        </label>
        <label class="fc-field grow"><span>BUSCAR TAG DA LINHA</span>
          <input id="f-search" type="search" value="${this.esc(this.search)}" placeholder="Ex.: 4&quot;-32-GC-001-BA">
        </label>
      </div>`;
  }

  /** Ações em massa (item 20) — só para quem pode apontar avanço. */
  private bulkBarHtml(): string {
    if (!this.can(PermissionCode.FieldControlUpdateOwn)) return "";
    return `
      <div class="fc-bulk">
        <span class="fc-bulk-count">${this.selected.size} selecionada(s)</span>
        <button class="fc-btn" id="b-set" type="button">DEFINIR AVANÇO</button>
        <button class="fc-btn ok" id="b-done" type="button">CONCLUIR SELECIONADAS</button>
        <button class="fc-btn muted" id="b-out" type="button">MARCAR FORA DE OPERAÇÃO</button>
      </div>`;
  }

  private visibleRows(): FieldControlLineRow[] {
    const search = this.search.trim().toUpperCase();
    return (this.scope?.rows || []).filter(row => {
      if (this.filterSystem && row.systemId !== this.filterSystem) return false;
      if (this.filterStatus && row.status !== this.filterStatus) return false;
      if (search && !row.lineTag.toUpperCase().includes(search)) return false;
      return true;
    });
  }

  /** Tabela operacional (item 9). No celular vira cartão, sem rolagem lateral (item 42). */
  private tableHtml(): string {
    const rows = this.visibleRows();
    if (rows.length === 0) {
      return `<div class="fc-empty">${this.tab === "minhas"
        ? "Nenhum Sistema de Tubulação vinculado a você. Solicite o vínculo ao Técnico ou Engenheiro responsável."
        : "Nenhuma Linha no recorte selecionado."}</div>`;
    }
    const canUpdate = this.can(PermissionCode.FieldControlUpdateOwn);
    return `
      <table class="fc-table">
        <thead>
          <tr>
            ${canUpdate ? `<th class="sel"><input type="checkbox" id="sel-all"></th>` : ""}
            <th>TAG DA LINHA</th><th class="hide-sm">SISTEMA</th><th class="hide-sm">FLUIDO</th>
            <th class="hide-md">DE</th><th class="hide-md">PARA</th>
            <th class="hide-sm">INÍCIO</th><th class="hide-sm">TÉRMINO</th>
            <th>AVANÇO</th><th>STATUS</th>
            <th class="hide-md">ÚLTIMA ATUALIZAÇÃO</th><th class="hide-md">OBSERVAÇÃO</th>
            ${canUpdate ? `<th>AÇÃO</th>` : ""}
          </tr>
        </thead>
        <tbody>
          ${rows.map(row => this.rowHtml(row, canUpdate)).join("")}
        </tbody>
      </table>`;
  }

  private rowHtml(row: FieldControlLineRow, canUpdate: boolean): string {
    const p = row.progress;
    const id = this.esc(row.lineEquipmentId);
    return `
      <tr data-line="${id}" class="${p.outOfOperation ? "off" : ""}">
        ${canUpdate ? `<td class="sel"><input type="checkbox" class="row-sel" data-line="${id}" ${this.selected.has(row.lineEquipmentId) ? "checked" : ""}></td>` : ""}
        <td class="tag">${this.esc(row.lineTag)}</td>
        <td class="hide-sm">${this.esc(row.systemTag)}</td>
        <td class="hide-sm">${this.esc(row.fluid || "—")}</td>
        <td class="hide-md">${this.esc(row.from || "—")}</td>
        <td class="hide-md">${this.esc(row.to || "—")}</td>
        <td class="hide-sm">${this.formatDate(p.startedAt)}</td>
        <td class="hide-sm">${this.formatDate(p.completedAt)}</td>
        <td class="progress">
          ${canUpdate
            ? `<div class="fc-progress">
                 <input type="range" min="0" max="100" step="1" value="${p.progressPercent}" class="slider" data-line="${id}" ${p.outOfOperation ? "disabled" : ""}>
                 <input type="number" min="0" max="100" step="1" value="${p.progressPercent}" class="pct" data-line="${id}" ${p.outOfOperation ? "disabled" : ""}>
               </div>`
            : `<div class="bar"><span style="width:${p.progressPercent}%"></span></div><small>${p.progressPercent}%</small>`}
        </td>
        <td><span class="fc-status ${this.statusClass(row.status)}">${this.esc(row.status)}</span></td>
        <td class="hide-md">${this.formatDate(p.updatedAt)}</td>
        <td class="hide-md note">${this.esc(p.fieldNote || p.outOfOperationReason || "—")}</td>
        ${canUpdate ? `
          <td class="actions">
            ${p.outOfOperation
              ? `<button class="fc-link" data-act="return" data-line="${id}" type="button">RETORNAR À OPERAÇÃO</button>`
              : `<button class="fc-link ok" data-act="done" data-line="${id}" type="button">CONCLUIR</button>
                 <button class="fc-link muted" data-act="out" data-line="${id}" type="button">FORA DE OPERAÇÃO</button>`}
            <button class="fc-link" data-act="note" data-line="${id}" type="button">OBSERVAÇÃO</button>
          </td>` : ""}
      </tr>`;
  }

  private managementHtml(): string {
    const options = (list: Option[], selected: string, placeholder: string) =>
      `<option value="">${placeholder}</option>` +
      list.map(o => `<option value="${this.esc(o.id)}" ${selected === o.id ? "selected" : ""}>${this.esc(o.label)}</option>`).join("");
    const inspectorName = (id: string) => this.inspectors.find(i => i.id === id)?.label || id;
    const systemName = (id: string | null) => id ? (this.systems.find(s => s.id === id)?.label || id) : "NÃO APLICÁVEL";
    const targetLabel = (a: { targetType: "SYSTEM" | "LINE"; targetId: string; systemId: string | null }) => a.targetType === "LINE" ? (this.lines.find(l => l.id === a.targetId)?.label || a.targetId) : systemName(a.systemId);
    return `
      <section class="fc-panel">
        <h2 class="fc-panel-title">VINCULAR INSPETOR AO SISTEMA OU LINHA</h2>
        <p class="fc-panel-sub">O Inspetor passa a ver as Linhas do Sistema vinculado ou a Linha sem Sistema atribuída diretamente.</p>
        <div class="fc-cascade">
          <label class="fc-field"><span>CLIENTE</span><select id="m-client">${options(this.clients, this.form.clientId, "Selecione")}</select></label>
          <label class="fc-field"><span>OS</span><select id="m-os">${options(this.workOrders, this.form.serviceOrderId, "Opcional")}</select></label>
          <label class="fc-field"><span>PROJETO</span><select id="m-project">${options(this.projects, this.form.projectId, "Opcional")}</select></label>
          <label class="fc-field"><span>UNIDADE / ÁREA</span><select id="m-unit">${options(this.unitAreas, this.form.unitAreaId, "Opcional")}</select></label>
          <label class="fc-field"><span>TIPO DE ALVO</span><select id="m-target-type"><option value="SYSTEM" ${this.form.targetType === "SYSTEM" ? "selected" : ""}>SISTEMA DE TUBULAÇÃO</option><option value="LINE" ${this.form.targetType === "LINE" ? "selected" : ""}>LINHA — SISTEMA NÃO APLICÁVEL</option></select></label>
          ${this.form.targetType === "LINE"
            ? `<label class="fc-field"><span>LINHA / TAG</span><select id="m-line">${options(this.lines, this.form.targetId, "Selecione")}</select></label>`
            : `<label class="fc-field"><span>SISTEMA DE TUBULAÇÃO</span><select id="m-system">${options(this.systems, this.form.targetId || this.form.systemId, "Selecione")}</select></label>`}
          <label class="fc-field"><span>INSPETOR</span><select id="m-inspector">${options(this.inspectors, this.form.inspectorProfessionalId, "Selecione")}</select></label>
        </div>
        <button class="fc-btn ok" id="m-save" type="button">VINCULAR</button>
      </section>

      <section class="fc-panel">
        <h2 class="fc-panel-title">VÍNCULOS EXISTENTES (${this.assignments.length})</h2>
        ${this.assignments.length === 0 ? `<div class="fc-empty">Nenhum vínculo cadastrado.</div>` : `
          <table class="fc-table">
            <thead><tr><th>INSPETOR</th><th>SISTEMA</th><th>SITUAÇÃO</th><th>AÇÃO</th></tr></thead>
            <tbody>
              ${this.assignments.map(a => `
                <tr>
                  <td>${this.esc(inspectorName(a.inspectorProfessionalId))}</td>
                  <td class="tag">${this.esc(targetLabel(a))}</td>
                  <td><span class="fc-status ${a.active ? "ok" : "muted"}">${a.active ? "ATIVO" : "INATIVO"}</span></td>
                  <td>${a.active ? `<button class="fc-link muted" data-unassign="${this.esc(a.id)}" type="button">DESVINCULAR</button>` : "—"}</td>
                </tr>`).join("")}
            </tbody>
          </table>`}
      </section>`;
  }

  // ------------------------------------------------------------- binding

  private bind(): void {
    this.querySelectorAll(".fc-tab").forEach(button =>
      button.addEventListener("click", () => this.setTab((button as HTMLElement).dataset["tab"] as Tab)));

    const onFilter = (id: string, apply: (value: string) => void, reload: boolean) => {
      const element = this.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement | null;
      element?.addEventListener("change", () => { apply(element.value); if (reload) void this.reload(); else this.render(); });
    };
    onFilter("f-inspector", value => { this.filterInspector = value; }, true);
    onFilter("f-system", value => { this.filterSystem = value; }, false);
    onFilter("f-status", value => { this.filterStatus = value; }, false);
    const search = this.querySelector("#f-search") as HTMLInputElement | null;
    search?.addEventListener("input", () => { this.search = search.value; this.render(); (this.querySelector("#f-search") as HTMLInputElement)?.focus(); });

    this.querySelectorAll(".row-sel").forEach(box => box.addEventListener("change", () => {
      const line = (box as HTMLElement).dataset["line"]!;
      if ((box as HTMLInputElement).checked) this.selected.add(line); else this.selected.delete(line);
      const counter = this.querySelector(".fc-bulk-count");
      if (counter) counter.textContent = `${this.selected.size} selecionada(s)`;
    }));
    (this.querySelector("#sel-all") as HTMLInputElement | null)?.addEventListener("change", event => {
      const checked = (event.target as HTMLInputElement).checked;
      this.selected = checked ? new Set(this.visibleRows().map(row => row.lineEquipmentId)) : new Set();
      this.render();
    });

    // Slider e campo numérico são a mesma intenção: gravam ao soltar/sair.
    this.querySelectorAll(".slider").forEach(slider => slider.addEventListener("change", () =>
      void this.applyChange((slider as HTMLElement).dataset["line"]!, { progressPercent: Number((slider as HTMLInputElement).value) })));
    this.querySelectorAll(".pct").forEach(input => input.addEventListener("change", () =>
      void this.applyChange((input as HTMLElement).dataset["line"]!, { progressPercent: (input as HTMLInputElement).value })));

    this.querySelectorAll("[data-act]").forEach(button => button.addEventListener("click", () => {
      const line = (button as HTMLElement).dataset["line"]!;
      const action = (button as HTMLElement).dataset["act"];
      if (action === "done") {
        if (confirm("Concluir a inspeção desta Linha? O avanço vai para 100% e a data de término será hoje.")) {
          void this.applyChange(line, { progressPercent: 100 });
        }
        return;
      }
      if (action === "out") {
        const reason = prompt("Motivo para marcar a Linha como FORA DE OPERAÇÃO:");
        if (reason && reason.trim()) void this.applyChange(line, { outOfOperation: true, outOfOperationReason: reason.trim() });
        return;
      }
      if (action === "return") {
        if (confirm("Retornar esta Linha à operação? O avanço anterior é preservado.")) {
          void this.applyChange(line, { outOfOperation: false });
        }
        return;
      }
      if (action === "note") {
        const note = prompt("Observação de campo:", this.rowOf(line)?.progress.fieldNote || "");
        if (note !== null) void this.applyChange(line, { fieldNote: note.trim() || null });
      }
    }));

    this.querySelector("#b-set")?.addEventListener("click", () => {
      const value = prompt("Definir avanço (0 a 100) para as Linhas selecionadas:");
      if (value !== null && value.trim()) void this.bulk({ progressPercent: value.trim() }, "Confirmar novo avanço?");
    });
    this.querySelector("#b-done")?.addEventListener("click", () => void this.bulk({ progressPercent: 100 }, "Concluir as Linhas selecionadas?"));
    this.querySelector("#b-out")?.addEventListener("click", () => {
      const reason = prompt("Motivo para marcar as Linhas selecionadas como FORA DE OPERAÇÃO:");
      if (reason && reason.trim()) void this.bulk({ outOfOperation: true, outOfOperationReason: reason.trim() }, "Confirmar FORA DE OPERAÇÃO?");
    });

    this.bindManagement();
  }

  private bindManagement(): void {
    const bindSelect = (id: string, apply: (value: string) => void, reloadCascade: boolean) => {
      const element = this.querySelector(`#${id}`) as HTMLSelectElement | null;
      element?.addEventListener("change", async () => {
        apply(element.value);
        if (reloadCascade) { await this.loadCascade(); }
        this.render();
      });
    };
    bindSelect("m-client", value => { this.form.clientId = value; this.form.projectId = ""; this.form.unitAreaId = ""; this.form.systemId = ""; }, true);
    bindSelect("m-os", value => { this.form.serviceOrderId = value; }, false);
    bindSelect("m-project", value => { this.form.projectId = value; this.form.unitAreaId = ""; this.form.systemId = ""; }, true);
    bindSelect("m-unit", value => { this.form.unitAreaId = value; this.form.systemId = ""; }, true);
    bindSelect("m-target-type", value => {
      this.form.targetType = value as "SYSTEM" | "LINE";
      this.form.targetId = "";
      this.form.systemId = "";
      this.render();
    }, false);
    bindSelect("m-system", value => { this.form.targetId = value; this.form.systemId = value; }, false);
    bindSelect("m-line", value => { this.form.targetId = value; this.form.systemId = ""; }, false);
    bindSelect("m-inspector", value => { this.form.inspectorProfessionalId = value; }, false);

    this.querySelector("#m-save")?.addEventListener("click", async () => {
      try {
        await this.platform.fieldControlService.createAssignment(this.ctx(), {
          inspectorProfessionalId: this.form.inspectorProfessionalId,
          clientId: this.form.clientId,
          serviceOrderId: this.form.serviceOrderId || null,
          projectId: this.form.projectId || null,
          unitAreaId: this.form.unitAreaId || null,
          targetType: this.form.targetType,
          targetId: this.form.targetId,
          systemId: this.form.targetType === "SYSTEM" ? this.form.targetId : null
        });
        this.message = { kind: "ok", text: "Inspetor vinculado ao Sistema." };
      } catch (error) {
        this.message = { kind: "error", text: this.errorText(error) };
      }
      await this.reload();
    });

    this.querySelectorAll("[data-unassign]").forEach(button => button.addEventListener("click", async () => {
      if (!confirm("Desvincular este Inspetor do Sistema?")) return;
      try {
        await this.platform.fieldControlService.deactivateAssignment(this.ctx(), (button as HTMLElement).dataset["unassign"]);
        this.message = { kind: "ok", text: "Vínculo desativado." };
      } catch (error) {
        this.message = { kind: "error", text: this.errorText(error) };
      }
      await this.reload();
    }));
  }

  // ------------------------------------------------------------- helpers

  private statusClass(status: string): string {
    if (status === FieldInspectionStatus.Completed) return "ok";
    if (status === FieldInspectionStatus.InProgress) return "warn";
    if (status === FieldInspectionStatus.OutOfOperation) return "muted";
    return "";
  }

  private formatPercent(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
  }

  private formatDate(value: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
  }

  private esc(value: unknown): string {
    return String(value ?? "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .fc { padding: 20px; max-width: 1500px; margin: 0 auto; color: var(--color-text-primary); }
      .fc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
      .fc-title { margin: 0; font-size: 1.3rem; font-weight: 700; letter-spacing: 0.04em; }
      .fc-sub { margin: 4px 0 0; font-size: 0.8rem; color: var(--color-text-disabled); }
      .fc-tabs { display: flex; gap: 6px; flex-wrap: wrap; border-bottom: 1px solid var(--color-graphite-300); margin-bottom: 14px; }
      .fc-tab { background: none; border: none; border-bottom: 2px solid transparent; padding: 9px 14px; font-size: 0.76rem; font-weight: 600;
                letter-spacing: 0.05em; color: var(--color-text-disabled); cursor: pointer; }
      .fc-tab.active { color: var(--color-text-primary); border-bottom-color: var(--color-primary); }
      .fc-notice { padding: 10px 14px; border-radius: 6px; font-size: 0.8rem; margin-bottom: 12px; }
      .fc-notice.ok { background: rgba(16,185,129,0.12); color: var(--status-success-text); }
      .fc-notice.error { background: rgba(239,68,68,0.12); color: var(--status-danger-text); }
      .fc-notice.info { background: rgba(59,130,246,0.12); color: var(--color-text-secondary); }
      .fc-empty { padding: 28px; text-align: center; color: var(--color-text-disabled); font-size: 0.85rem; }

      .fc-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-bottom: 14px; }
      .fc-card { border: 1px solid var(--color-graphite-300); border-radius: 8px; padding: 10px 12px; display: flex; flex-direction: column; gap: 4px; }
      .fc-card-label { font-size: 0.64rem; letter-spacing: 0.07em; color: var(--color-text-disabled); }
      .fc-card-value { font-size: 1.25rem; font-weight: 700; }
      .fc-card.ok .fc-card-value { color: var(--status-success-text); }
      .fc-card.warn .fc-card-value { color: var(--status-warning-text); }
      .fc-card.muted .fc-card-value { color: var(--color-text-disabled); }
      .fc-card.accent .fc-card-value { color: var(--ia-primary); }

      .fc-filters, .fc-cascade { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin-bottom: 12px; }
      .fc-field { display: flex; flex-direction: column; gap: 4px; font-size: 0.68rem; letter-spacing: 0.06em; color: var(--color-text-disabled); }
      .fc-field.grow { grid-column: span 2; }
      .fc-field select, .fc-field input { padding: 7px 10px; border: 1px solid var(--color-input-border); border-radius: 6px;
                                          background: var(--color-input-bg); color: var(--color-text-primary); font-size: 0.82rem; }

      .fc-bulk { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
      .fc-bulk-count { font-size: 0.72rem; color: var(--color-text-disabled); }
      .fc-btn { padding: 7px 12px; border-radius: 6px; border: 1px solid var(--color-graphite-300); background: var(--color-surface-secondary);
                color: var(--color-text-primary); font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em; cursor: pointer; }
      .fc-btn.ok { border-color: rgba(16,185,129,0.4); color: var(--status-success-text); }
      .fc-btn.muted { color: var(--color-text-disabled); }

      .fc-table { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
      .fc-table th { text-align: left; font-size: 0.62rem; letter-spacing: 0.07em; text-transform: uppercase; color: var(--color-text-disabled);
                     padding: 8px 6px; border-bottom: 1px solid var(--color-graphite-300); white-space: normal; }
      .fc-table td { padding: 8px 6px; border-bottom: 1px solid rgba(255,255,255,0.05); vertical-align: middle; }
      .fc-table tr.off td { opacity: 0.55; }
      .fc-table .tag { font-family: monospace; font-weight: 600; }
      .fc-table .sel { width: 28px; }
      .fc-table .note { max-width: 220px; }
      .fc-progress { display: flex; align-items: center; gap: 6px; }
      .fc-progress .slider { width: 90px; }
      .fc-progress .pct { width: 56px; padding: 4px 6px; border: 1px solid var(--color-input-border); border-radius: 4px;
                          background: var(--color-input-bg); color: var(--color-text-primary); }
      .bar { width: 90px; height: 6px; border-radius: 3px; background: rgba(255,255,255,0.1); overflow: hidden; }
      .bar span { display: block; height: 100%; background: var(--color-primary); }
      .fc-status { font-size: 0.64rem; font-weight: 700; letter-spacing: 0.05em; padding: 2px 7px; border-radius: 999px;
                   background: rgba(255,255,255,0.06); white-space: nowrap; }
      .fc-status.ok { background: rgba(16,185,129,0.15); color: var(--status-success-text); }
      .fc-status.warn { background: rgba(245,158,11,0.15); color: var(--status-warning-text); }
      .fc-status.muted { color: var(--color-text-disabled); }
      .fc-link { background: none; border: none; padding: 0 6px 0 0; font-size: 0.66rem; font-weight: 600; letter-spacing: 0.04em;
                 color: var(--color-text-secondary); cursor: pointer; }
      .fc-link.ok { color: var(--status-success-text); }
      .fc-link.muted { color: var(--color-text-disabled); }
      .actions { white-space: nowrap; }

      .fc-panel { border: 1px solid var(--color-graphite-300); border-radius: 8px; padding: 14px; margin-bottom: 14px; }
      .fc-panel-title { margin: 0 0 4px; font-size: 0.8rem; font-weight: 700; letter-spacing: 0.05em; }
      .fc-panel-sub { margin: 0 0 12px; font-size: 0.74rem; color: var(--color-text-disabled); }

      /* Campo: no celular o essencial é TAG, AVANÇO, STATUS e as ações
         rápidas — o resto sai da tela em vez de virar rolagem lateral. */
      @media (max-width: 900px) { .hide-md { display: none; } .fc-field.grow { grid-column: span 1; } }
      @media (max-width: 640px) {
        .fc { padding: 12px; }
        .hide-sm { display: none; }
        .fc-table { font-size: 0.82rem; }
        .fc-progress .slider { width: 70px; }
        .actions { white-space: normal; }
        .fc-cards { grid-template-columns: repeat(2, 1fr); }
      }`;
  }
}

if (!customElements.get("app-field-control-page")) {
  customElements.define("app-field-control-page", AppFieldControlPage);
}
